import { createContext, useContext, useEffect, useState, useMemo } from "react";

const STRINGS = {
  en: {
    dir: "ltr",
    nav: {
      capabilities: "Capabilities",
      about: "About",
      approach: "Approach",
      contact: "Contact",
      cta: "Talk to us",
      langLabel: "EN",
      langAlt: "العربية",
    },
    hero: {
      title1: "Ship applied AI",
      title_em: "where it counts —",
      title2: "agents, apps, and the work behind them.",
      sub: "Applied AI for real operational problems. We build the agents, the apps, and the systems that make them work — on Emergent, shipped in weeks.",
      ctaPrimary: "Talk to us",
      partnerChip: "Partnered with",
    },
    clients: {
      eyebrow: "/ Clients",
      title_a: "Teams that trust us with",
      title_b: "the work.",
      meta: "Selected engagements",
    },
    about: {
      eyebrow: "/ About",
      title_a: "Applied AI only works",
      title_em: "when the",
      title_b: "workflow does.",
      principles: [
        {
          t: "Workflow first",
          d: "We start with the work your teams actually run — not the org chart above them.",
        },
        {
          t: "Systems, not slideware",
          d: "We deliver executable systems your operators use every shift.",
        },
        {
          t: "Shipped in weeks",
          d: "Built on Emergent, so we spend the quarter on your problem — not on infra.",
        },
      ],
    },
    caps: {
      eyebrow: "/ Capabilities",
      title_a: "What we build for",
      title_b: "mission-critical operations.",
      cards: {
        agents: {
          title: "Agentic AI systems",
          desc: "Primary agents orchestrating specialised sub-agents, with human-in-the-loop where it matters.",
        },
        mobile: {
          title: "Bilingual mobile apps",
          desc: "iOS, Android and web from one codebase. English and full RTL Arabic by default.",
        },
        ops: {
          title: "Live operations dashboards",
          desc: "Command portals with real-time telemetry, exception routing and role-based access.",
        },
        workflows: {
          title: "Workflow automation",
          desc: "We parse the operational workflow, then automate the high-leverage steps end to end.",
        },
      },
    },
    process: {
      eyebrow: "/ Approach",
      title_a: "From workflow to",
      title_em: "executable system.",
      sub: "Time-boxed, partner-reviewed phases. No open-ended retainers.",
      steps: [
        { title: "Map the workflow", desc: "Two weeks with the team that actually runs the work." },
        { title: "Find the leverage", desc: "The two or three decisions where AI changes the economics." },
        { title: "Build the system", desc: "Prototype on real data. Production on Emergent." },
        { title: "Run & refine", desc: "Monthly reviews. Quarterly expansion. Named owners throughout." },
      ],
    },
    contact: {
      eyebrow: "/ Get in touch",
      title_a: "Bring us a workflow worth",
      title_em: "automating.",
      sub: "Tell us what you want to transform. A partner will respond within one business day.",
      fields: {
        name: "Name",
        namePh: "Your name",
        email: "Work email",
        emailPh: "you@company.com",
        company: "Company",
        companyPh: "Your organisation",
        interest: "Area of interest",
        interestPh: "Select a focus area",
        message: "What are you trying to solve?",
        messagePh: "A few sentences on the workflow…",
      },
      interests: {
        sports: "AI in Sports",
        infra: "AI in Infrastructure",
        finance: "AI in Finance",
        fraud: "AI in Fraud Detection",
        diligence: "AI in Due Diligence",
        other: "Something else",
      },
      submit: "Send message",
      sending: "Sending…",
      nda: "By submitting you agree to our standard NDA terms.",
      loc: "New Delhi · India",
      successToast: "Thanks — a partner will reply within one business day.",
      errorToast: "Something went wrong. Please try again.",
      missingToast: "Please fill in name, email and message.",
    },
    footer: {
      tag_a: "Operational workflows,",
      tag_b: "turned into AI systems.",
      sub: "Applied AI studio. New Delhi.",
      poweredBy: "Partnered with",
      rights: "All rights reserved",
      practices: [
        "AI in Events",
        "AI in Infrastructure",
        "AI in Sports",
        "AI in Finance",
        "AI in Fraud Detection",
        "AI in Due Diligence",
      ],
      cols: {
        about: "Delhigence.AI",
        practices: "Domains",
        contact: "Get in touch",
        links: {
          about: "About",
          approach: "Approach",
          contact: "Contact",
        },
      },
    },
  },

  ar: {
    dir: "rtl",
    nav: {
      capabilities: "القدرات",
      about: "من نحن",
      approach: "المنهجية",
      contact: "تواصل",
      cta: "تحدث معنا",
      langLabel: "AR",
      langAlt: "English",
    },
    hero: {
      title1: "أطلق ذكاءً اصطناعيًا تطبيقيًا",
      title_em: "حيث يُحدث فارقًا —",
      title2: "وكلاء، وتطبيقات، والعمل الذي يقف خلفها.",
      sub: "ذكاء اصطناعي تطبيقي للمشكلات التشغيلية الحقيقية. نبني الوكلاء والتطبيقات والأنظمة التي تجعلها تعمل — على Emergent، وتُسلَّم في أسابيع.",
      ctaPrimary: "تحدث معنا",
      partnerChip: "بالشراكة مع",
    },
    clients: {
      eyebrow: "/ العملاء",
      title_a: "فرق ائتمنتنا",
      title_b: "على العمل.",
      meta: "تكليفات مختارة",
    },
    about: {
      eyebrow: "/ من نحن",
      title_a: "الذكاء الاصطناعي التطبيقي",
      title_em: "لا ينجح",
      title_b: "إلا حين ينجح سير العمل.",
      principles: [
        {
          t: "سير العمل أولاً",
          d: "نبدأ من العمل الذي يُنفّذه فريقك فعلاً — لا من الهيكل التنظيمي فوقه.",
        },
        {
          t: "أنظمة لا عروض",
          d: "نُسلّم أنظمة قابلة للتنفيذ يستخدمها فريقك في كل وردية.",
        },
        {
          t: "تُسلَّم في أسابيع",
          d: "مبني على Emergent، فنُكرّس الربع كامله لمشكلتك، لا للبنية التحتية.",
        },
      ],
    },
    caps: {
      eyebrow: "/ القدرات",
      title_a: "ما نبنيه للعمليات",
      title_b: "الحيوية والحساسة.",
      cards: {
        agents: {
          title: "أنظمة الوكلاء الذكية",
          desc: "وكلاء رئيسيون يُنسّقون وكلاء متخصصين، مع تدخل بشري حيث يهم.",
        },
        mobile: {
          title: "تطبيقات جوّال ثنائية اللغة",
          desc: "iOS و Android والويب من قاعدة كود واحدة. الإنجليزية والعربية RTL افتراضيًا.",
        },
        ops: {
          title: "لوحات تحكم للعمليات الحية",
          desc: "بوابات قيادة بقياسات فورية، وتوجيه للاستثناءات، وصلاحيات مبنية على الأدوار.",
        },
        workflows: {
          title: "أتمتة سير العمل",
          desc: "نُحلّل سير العمل التشغيلي، ثم نُؤتمت الخطوات الحاسمة من طرفها إلى طرفها.",
        },
      },
    },
    process: {
      eyebrow: "/ المنهجية",
      title_a: "من سير العمل إلى",
      title_em: "نظام قابل للتنفيذ.",
      sub: "مراحل محددة زمنيًا يُراجعها الشركاء. لا عقود مفتوحة.",
      steps: [
        { title: "تخطيط سير العمل", desc: "أسبوعان مع الفريق الذي يُنفّذ العمل فعلاً." },
        { title: "تحديد نقاط الرافعة", desc: "القرارات الاثنان أو الثلاثة التي يُغيّر الذكاء الاصطناعي فيها المعادلة." },
        { title: "بناء النظام", desc: "نموذج أولي على بيانات حقيقية. النشر على Emergent." },
        { title: "التشغيل والتحسين", desc: "مراجعات شهرية. توسّع ربعي. مسؤولون مُسمَّون طوال الوقت." },
      ],
    },
    contact: {
      eyebrow: "/ تواصل معنا",
      title_a: "شاركنا سير عمل يستحق",
      title_em: "الأتمتة.",
      sub: "أخبرنا بما تريد تحويله. سيرد شريك خلال يوم عمل واحد.",
      fields: {
        name: "الاسم",
        namePh: "اسمك",
        email: "البريد الإلكتروني",
        emailPh: "name@company.com",
        company: "الشركة",
        companyPh: "اسم مؤسستك",
        interest: "مجال الاهتمام",
        interestPh: "اختر مجالًا",
        message: "ما الذي تحاول حلّه؟",
        messagePh: "بضع جمل عن سير العمل…",
      },
      interests: {
        sports: "الذكاء الاصطناعي في الرياضة",
        infra: "الذكاء الاصطناعي في البنية التحتية",
        finance: "الذكاء الاصطناعي في التمويل",
        fraud: "الذكاء الاصطناعي في كشف الاحتيال",
        diligence: "الذكاء الاصطناعي في العناية الواجبة",
        other: "شيء آخر",
      },
      submit: "أرسل الرسالة",
      sending: "جارٍ الإرسال…",
      nda: "بإرسالك النموذج فأنت توافق على شروط اتفاقية عدم الإفصاح.",
      loc: "نيو دلهي · الهند",
      successToast: "شكرًا — سيرد شريك خلال يوم عمل واحد.",
      errorToast: "حدث خطأ. حاول مرة أخرى.",
      missingToast: "يرجى تعبئة الاسم والبريد والرسالة.",
    },
    footer: {
      tag_a: "سير العمل التشغيلي،",
      tag_b: "يتحوّل إلى أنظمة ذكاء اصطناعي.",
      sub: "استوديو ذكاء اصطناعي تطبيقي. نيو دلهي.",
      poweredBy: "بالشراكة مع",
      rights: "جميع الحقوق محفوظة",
      practices: [
        "الذكاء الاصطناعي في الفعاليات",
        "الذكاء الاصطناعي في البنية التحتية",
        "الذكاء الاصطناعي في الرياضة",
        "الذكاء الاصطناعي في التمويل",
        "الذكاء الاصطناعي في كشف الاحتيال",
        "الذكاء الاصطناعي في العناية الواجبة",
      ],
      cols: {
        about: "دلهيجينس.AI",
        practices: "المجالات",
        contact: "تواصل معنا",
        links: {
          about: "من نحن",
          approach: "المنهجية",
          contact: "تواصل",
        },
      },
    },
  },
};

const LangCtx = createContext({ lang: "en", t: STRINGS.en, setLang: () => {} });

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("delhigence_lang") || "en";
  });

  useEffect(() => {
    localStorage.setItem("delhigence_lang", lang);
    const root = document.documentElement;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", STRINGS[lang].dir);
  }, [lang]);

  const value = useMemo(() => ({ lang, t: STRINGS[lang], setLang }), [lang]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);
